import React, { useEffect, useState } from 'react';
import { ExternalLink, Star } from 'lucide-react';

const SkeletonReview = () => (
  <div className="bg-white rounded-xl p-4 shadow-sm animate-pulse">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-stone-200 rounded-full skeleton"></div>
      <div className="flex-1">
        <div className="h-4 bg-stone-200 rounded w-24 skeleton mb-1"></div>
        <div className="h-3 bg-stone-200 rounded w-32 skeleton"></div>
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-stone-200 rounded w-full skeleton"></div>
      <div className="h-3 bg-stone-200 rounded w-4/5 skeleton"></div>
      <div className="h-3 bg-stone-200 rounded w-3/4 skeleton"></div>
    </div>
  </div>
);

const GoogleReviews = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const src = 'https://widgets.sociablekit.com/google-reviews/widget.js';
    // The prerendered HTML already contains this tag, so don't inject a second copy
    if (document.querySelector(`script[src="${src}"]`)) {
      const timer = setTimeout(() => setIsLoaded(true), 1500);
      return () => clearTimeout(timer);
    }

    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.onload = () => {
      // Give widget time to render
      setTimeout(() => setIsLoaded(true), 1500);
    };
    document.body.appendChild(script);
    return undefined;
  }, []);

  return (
    <section id="reviews" className="py-24 bg-gradient-warm relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-amber-100/50 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#3B43AF]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[#3B43AF] font-semibold text-sm uppercase tracking-wider mb-3 badge-pill">Testimonials</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-stone-900 mb-6">Our Happy Customers</h2>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Don't just take our word for it – see what our customers have to say about their experience with HappyVan.
          </p>
          
          {/* Google Rating Badge */}
          <div className="inline-flex items-center gap-4 bg-white rounded-full px-6 py-3 shadow-lg border border-stone-100">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
              alt="Google" 
              className="h-6 w-auto"
              width="92"
              height="30"
              loading="lazy"
              decoding="async"
            />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="font-bold text-stone-900">5.0</span>
            <span className="text-stone-500">rating</span>
          </div>
        </div>

        {/* Reviews Widget Container */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-4xl relative">
            {/* Skeleton loader - show while loading */}
            {!isLoaded && (
              <div className="grid md:grid-cols-2 gap-4 p-4">
                <SkeletonReview />
                <SkeletonReview />
                <SkeletonReview />
                <SkeletonReview />
              </div>
            )}

            {/* SociableKit Google Reviews Widget */}
            <div 
              className={`sk-ww-google-reviews rounded-2xl border border-stone-200 bg-white shadow-sm transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
              data-embed-id="25643272"
              style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}
            ></div>
          </div>
        </div>

        {/* View All Reviews Link */}
        <div className="text-center mt-8">
          <a 
            href="https://share.google/SPoCZSS7hLHds5fIk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border-2 border-stone-200 hover:border-[#3B43AF] text-stone-700 hover:text-[#3B43AF] px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
          >
            View All Reviews on Google
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
